import type React from "react";

import {
  Award,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  Clock,
  Coffee,
  Filter,
  Heart,
  Laptop,
  MapPin,
  Search,
  Sparkles,
  TrendingUp,
  Users,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { fetchJobPositions } from "@/services/landing/careersService";
import { fetchHeroBackgrounds } from "@/services/landing/heroBackgroundService";

interface JobPosition {
  application_deadline: string;
  application_url: string;
  benefits: string;
  created_at: string;
  department: string;
  employment_type: string;
  experience_level: string;
  hidden: number;
  icon: string;
  icon_color: string;
  id: number;
  job_description: string;
  location: string;
  preferred_qualifications: string;
  requirements: string;
  responsibilities: string;
  salary_max: number;
  salary_min: number;
  tags: null | string;
  title: string;
  updated_at: string;
  updated_by: string;
}

export default function Careers() {
  const benefits = [
    {
      description: "Comprehensive medical aid and wellness programs",
      icon: <Heart className="text-primary" size={32} />,
      title: "Health & Wellness",
    },
    {
      description: "Learning opportunities and clear advancement paths",
      icon: <TrendingUp className="text-success" size={32} />,
      title: "Career Growth",
    },
    {
      description: "Flexible hours and remote work options",
      icon: <Coffee className="text-warning" size={32} />,
      title: "Work-Life Balance",
    },
    {
      description: "Employee stock options and performance bonuses",
      icon: <Award className="text-info" size={32} />,
      title: "Equity & Recognition",
    },
  ];

  const values = [
    {
      description: "We ship quickly and iterate based on feedback",
      icon: <Zap className="text-primary" size={24} />,
      title: "Move Fast",
    },
    {
      description: "Every decision starts with our customers",
      icon: <Users className="text-secondary" size={24} />,
      title: "Customer Obsessed",
    },
    {
      description: "We challenge conventions and find new solutions",
      icon: <Sparkles className="text-accent" size={24} />,
      title: "Think Different",
    },
    {
      description: "We care about our impact on communities",
      icon: <Heart className="text-success" size={24} />,
      title: "Care Deeply",
    },
  ];

  // State for positions
  const [openPositions, setOpenPositions] = useState<JobPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);
  const [heroBackground, setHeroBackground] = useState<null | {
    imageAlt: string;
    imageUrl: string;
  }>(null);
  const [heroLoading, setHeroLoading] = useState(true);

  // Track which job is expanded
  const [expandedIndex, setExpandedIndex] = useState<null | number>(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState<string>("All");
  const [selectedLocation, setSelectedLocation] = useState<string>("All");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const positionsPerPage = 5;

  // Departments slider ref
  const sliderRef = useRef<HTMLDivElement>(null);

  // Fetch positions from API
  useEffect(() => {
    const loadPositions = async () => {
      try {
        setLoading(true);
        const data = await fetchJobPositions();
        // Filter out hidden positions
        const visiblePositions = Array.isArray(data)
          ? data.filter((pos: JobPosition) => pos.hidden === 0)
          : [];
        setOpenPositions(visiblePositions);
        setError(null);
      } catch (err) {
        console.error("Error fetching positions:", err);
        setError("Failed to load positions. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    loadPositions();
    fetchHeroBackgrounds("careers").then((data) => {
      setHeroBackground(data);
      setHeroLoading(false);
    });
  }, []);

  // Get unique departments and locations from positions
  const uniqueDepartments = Array.from(
    new Set(openPositions.map((pos) => pos.department)),
  );
  const uniqueLocations = Array.from(
    new Set(openPositions.map((pos) => pos.location)),
  );

  // Calculate real department counts
  const departmentCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    openPositions.forEach((pos) => {
      counts[pos.department] = (counts[pos.department] || 0) + 1;
    });
    return counts;
  }, [openPositions]);

  // Departments with real counts - sorted by count
  const departments = useMemo(() => {
    const deptList = [
      { color: "primary", name: "Operations" },
      { color: "secondary", name: "Fleet" },
      { color: "accent", name: "Product" },
      { color: "success", name: "Technology / IT" },
      { color: "warning", name: "Customer Experience" },
      { color: "info", name: "Business Development" },
      { color: "primary", name: "Strategy" },
      { color: "secondary", name: "Finance" },
      { color: "accent", name: "Administration" },
      { color: "success", name: "Human Resources" },
      { color: "warning", name: "Marketing" },
      { color: "info", name: "Growth" },
      { color: "primary", name: "Legal" },
      { color: "secondary", name: "Compliance" },
      { color: "accent", name: "Trust & Safety" },
      { color: "success", name: "Security" },
      { color: "warning", name: "Logistics" },
      { color: "info", name: "Supply Chain" },
      { color: "primary", name: "Procurement" },
      { color: "secondary", name: "Partnerships" },
      { color: "accent", name: "Data & Analytics" },
      { color: "success", name: "Risk Management" },
      { color: "warning", name: "Corporate Affairs" },
      { color: "info", name: "Executive Management" },
    ];

    return deptList
      .map((dept) => ({
        ...dept,
        count: departmentCounts[dept.name] || 0,
      }))
      .sort((a, b) => b.count - a.count); // Sort by count descending (highest first)
  }, [departmentCounts]);

  // Filter and search positions
  const filteredPositions = useMemo(() => {
    return openPositions.filter((job) => {
      const matchesSearch =
        job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.job_description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.requirements.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (job.tags &&
          job.tags.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesDepartment =
        selectedDepartment === "All" || job.department === selectedDepartment;
      const matchesLocation =
        selectedLocation === "All" || job.location === selectedLocation;

      return matchesSearch && matchesDepartment && matchesLocation;
    });
  }, [searchQuery, selectedDepartment, selectedLocation, openPositions]);

  // Calculate pagination based on filtered results
  const totalPages = Math.ceil(filteredPositions.length / positionsPerPage);
  const startIndex = (currentPage - 1) * positionsPerPage;
  const endIndex = startIndex + positionsPerPage;
  const currentPositions = filteredPositions.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setExpandedIndex(null);
    document
      .getElementById("open-positions")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  // Reset to page 1 when filters change
  const handleFilterChange = () => {
    setCurrentPage(1);
    setExpandedIndex(null);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    handleFilterChange();
  };

  const handleDepartmentChange = (value: string) => {
    setSelectedDepartment(value);
    handleFilterChange();
  };

  const handleLocationChange = (value: string) => {
    setSelectedLocation(value);
    handleFilterChange();
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedDepartment("All");
    setSelectedLocation("All");
    setCurrentPage(1);
    setExpandedIndex(null);
  };

  if (
    typeof document !== "undefined" &&
    !document.getElementById("hero-shimmer-style-careers")
  ) {
    const style = document.createElement("style");
    style.id = "hero-shimmer-style-careers";
    style.innerHTML = `
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .animate-shimmer {
        animation: shimmer 2s linear infinite;
      }
    `;
    document.head.appendChild(style);
  }

  const hasActiveFilters =
    searchQuery !== "" ||
    selectedDepartment !== "All" ||
    selectedLocation !== "All";

  // Slider controls
  const scroll = (direction: "left" | "right") => {
    if (sliderRef.current) {
      const scrollAmount = 300;
      sliderRef.current.scrollBy({
        behavior: "smooth",
        left: direction === "left" ? -scrollAmount : scrollAmount,
      });
    }
  };

  // Format salary range
  const formatSalary = (min: number, max: number) => {
    return `R${min.toLocaleString()} - R${max.toLocaleString()}`;
  };

  // Parse JSON array string and return as array, or return empty array if invalid
  const parseArrayField = (field: string | unknown): string[] => {
    if (!field) return [];
    if (typeof field !== "string") return [];

    try {
      const parsed = JSON.parse(field);
      return Array.isArray(parsed) ? parsed : [field];
    } catch {
      // If it's not valid JSON, return the original string as single item
      return [field];
    }
  };

  return (
    <>
      {/* Hero Background Wrapper - only covers navbar + hero section */}
      <div className="fixed top-0 left-0 right-0 z-0 pointer-events-none w-full" style={{ height: "600px" }}>
        <div className="absolute inset-0 opacity-20">
          {heroLoading ? (
            <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-primary/10 to-secondary/10">
              <div
                className="animate-shimmer absolute inset-0 bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10"
                style={{ backgroundSize: "200% 100%" }}
              />
            </div>
          ) : heroBackground?.imageUrl ? (
            <img
              alt={heroBackground?.imageAlt || "Hero background"}
              className="h-full w-full object-cover"
              src={heroBackground.imageUrl}
              style={{ display: heroBackground?.imageUrl ? "block" : "none" }}
            />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-primary/5 to-secondary/5" />
          )}
        </div>
      </div>

      <AppNavbar transparent />

      <div className="relative pt-16">
        <Breadcrumbs transparent />

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-transparent z-10">
          <div className="relative mx-auto max-w-7xl px-4 py-24">
            <div className="max-w-4xl">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-secondary-content/20 px-4 py-2">
                <Briefcase size={20} />
                <span className="font-medium">Join 500+ Team Members</span>
              </div>

              <h1 className="mb-6 text-5xl font-bold leading-tight lg:text-6xl">
                Build the future of{" "}
                <span className="text-primary">mobility with us</span>
              </h1>

              <p className="mb-8 text-xl leading-relaxed opacity-90">
                Join a diverse team of innovators, engineers, and dreamers who
                are transforming how people move across Africa. Make an impact
                while growing your career.
              </p>

              <div className="flex flex-col gap-4 sm:flex-row">
                <a className="btn btn-primary btn-lg" href="#open-positions">
                  View Open Roles
                </a>
                <a className="btn btn-outline btn-lg" href="#life-at-hareeng">
                  Life with us
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Departments Slider */}
        <section className="py-16 bg-base-100">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Teams at Hareeng</h2>
              <p className="text-xl text-base-content/70">
                Find your place in our growing organization
              </p>
            </div>

            <div className="relative">
              {/* Left Arrow */}
              <button
                aria-label="Scroll left"
                className="btn btn-circle btn-sm absolute left-0 top-1/2 z-10 -translate-y-1/2 bg-base-100 shadow-lg hover:bg-base-200"
                onClick={() => scroll("left")}
              >
                <ChevronLeft size={20} />
              </button>

              {/* Slider Container */}
              <div
                className="scrollbar-hide flex gap-4 overflow-x-auto scroll-smooth px-12 pb-4"
                ref={sliderRef}
                style={{ msOverflowStyle: "none", scrollbarWidth: "none" }}
              >
                {departments.map((dept, index) => (
                  <div
                    className={`card bg-${dept.color}/10 border border-${dept.color}/20 w-48 flex-shrink-0 cursor-pointer transition-all hover:scale-105 hover:shadow-lg ${dept.count === 0 ? "opacity-50" : ""}`}
                    key={index}
                    onClick={() =>
                      dept.count > 0 && handleDepartmentChange(dept.name)
                    }
                  >
                    <div className="card-body p-6 text-center">
                      <h3 className="text-sm font-semibold">{dept.name}</h3>
                      <p className="text-xs text-base-content/70">
                        {dept.count}{" "}
                        {dept.count === 1 ? "open role" : "open roles"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Right Arrow */}
              <button
                aria-label="Scroll right"
                className="btn btn-circle btn-sm absolute right-0 top-1/2 z-10 -translate-y-1/2 bg-base-100 shadow-lg hover:bg-base-200"
                onClick={() => scroll("right")}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section className="bg-base-100 py-16" id="open-positions">
          <div className="mx-auto max-w-7xl px-4">
            {/* Search and Filter Section */}
            <div className="mb-8">
              <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex-1">
                  <div className="relative">
                    <Search
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50"
                      size={20}
                    />
                    <input
                      className="input input-bordered w-full pl-10"
                      onChange={(e) => handleSearchChange(e.target.value)}
                      placeholder="Search positions, skills, or keywords..."
                      type="text"
                      value={searchQuery}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <Filter className="text-base-content/70" size={18} />
                    <span className="text-sm font-medium">Filters:</span>
                  </div>

                  <select
                    className="select select-bordered select-sm"
                    onChange={(e) => handleDepartmentChange(e.target.value)}
                    value={selectedDepartment}
                  >
                    <option value="All">All Departments</option>
                    {uniqueDepartments.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </select>

                  <select
                    className="select select-bordered select-sm"
                    onChange={(e) => handleLocationChange(e.target.value)}
                    value={selectedLocation}
                  >
                    <option value="All">All Locations</option>
                    {uniqueLocations.map((location) => (
                      <option key={location} value={location}>
                        {location}
                      </option>
                    ))}
                  </select>

                  {hasActiveFilters && (
                    <button
                      className="btn btn-ghost btn-sm gap-1"
                      onClick={clearFilters}
                    >
                      <X size={16} />
                      Clear Filters
                    </button>
                  )}
                </div>

                <div className="text-sm text-base-content/60">
                  {filteredPositions.length === openPositions.length ? (
                    <span>{openPositions.length} total positions</span>
                  ) : (
                    <span>
                      {filteredPositions.length} of {openPositions.length}{" "}
                      positions
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="flex justify-center py-16">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : error ? (
              <div className="py-16 text-center">
                <div className="alert alert-error mx-auto max-w-md">
                  <span>{error}</span>
                </div>
              </div>
            ) : filteredPositions.length > 0 ? (
              <>
                <div className="mb-4 text-center">
                  <p className="text-sm text-base-content/60">
                    Showing {startIndex + 1}-
                    {Math.min(endIndex, filteredPositions.length)} of{" "}
                    {filteredPositions.length} positions
                  </p>
                </div>

                <div className="space-y-4">
                  {currentPositions.map((job, index) => {
                    const globalIndex = filteredPositions.indexOf(job);
                    return (
                      <div
                        className="card bg-base-200 shadow-lg transition-shadow hover:shadow-xl"
                        key={job.id}
                      >
                        <div className="card-body">
                          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                            <div className="flex-1">
                              <div className="mb-2 flex flex-wrap items-center gap-4">
                                <h3 className="card-title text-xl">
                                  {job.title}
                                </h3>
                                <span className="badge badge-primary">
                                  {job.department}
                                </span>
                                <span className="badge badge-outline">
                                  {job.employment_type}
                                </span>
                                <span className="badge badge-accent">
                                  {job.experience_level}
                                </span>
                              </div>

                              <div className="mb-3 flex flex-wrap items-center gap-4 text-sm text-base-content/70">
                                <div className="flex items-center gap-1">
                                  <MapPin size={16} />
                                  <span>{job.location}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Clock size={16} />
                                  <span>{job.employment_type}</span>
                                </div>
                                {job.salary_min && job.salary_max && (
                                  <div className="flex items-center gap-1">
                                    <Award size={16} />
                                    <span>
                                      {formatSalary(
                                        job.salary_min,
                                        job.salary_max,
                                      )}
                                    </span>
                                  </div>
                                )}
                              </div>

                              <p className="mb-3 text-base-content/80">
                                {job.job_description}
                              </p>

                              {job.tags && (
                                <div className="flex flex-wrap gap-2">
                                  {parseArrayField(job.tags).map(
                                    (tag, tagIndex) => (
                                      <span
                                        className="badge badge-ghost badge-sm"
                                        key={tagIndex}
                                      >
                                        {tag.trim()}
                                      </span>
                                    ),
                                  )}
                                </div>
                              )}

                              {/* Application Link when collapsed */}
                              {expandedIndex !== globalIndex &&
                                job.application_url && (
                                  <p className="mt-3 text-sm text-base-content/70">
                                    Follow this instruction to apply:{" "}
                                    {job.application_url}
                                  </p>
                                )}
                            </div>

                            <div className="mt-4 lg:ml-6 lg:mt-0">
                              <button
                                className="btn btn-primary"
                                onClick={() =>
                                  setExpandedIndex(
                                    expandedIndex === globalIndex
                                      ? null
                                      : globalIndex,
                                  )
                                }
                              >
                                {expandedIndex === globalIndex
                                  ? "Hide Details"
                                  : "View Details"}
                                <ChevronRight size={16} />
                              </button>
                            </div>
                          </div>
                          {/* Collapsible submission details */}
                          {expandedIndex === globalIndex && (
                            <div className="mt-6 rounded-xl border border-primary/20 bg-base-100 p-6">
                              <h4 className="mb-4 text-lg font-semibold text-primary">
                                Position Details
                              </h4>

                              <div className="space-y-4">
                                <div>
                                  <span className="font-bold">
                                    Application Deadline:
                                  </span>{" "}
                                  {new Date(
                                    job.application_deadline,
                                  ).toLocaleDateString()}
                                </div>

                                {job.responsibilities && (
                                  <div>
                                    <span className="font-bold">
                                      Responsibilities:
                                    </span>
                                    <ul className="ml-4 mt-2 list-disc space-y-1 text-base-content/80">
                                      {parseArrayField(
                                        job.responsibilities,
                                      ).map((item, idx) => (
                                        <li key={idx}>{item.trim()}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {job.requirements && (
                                  <div>
                                    <span className="font-bold">
                                      Requirements:
                                    </span>
                                    <ul className="ml-4 mt-2 list-disc space-y-1 text-base-content/80">
                                      {parseArrayField(job.requirements).map(
                                        (item, idx) => (
                                          <li key={idx}>{item.trim()}</li>
                                        ),
                                      )}
                                    </ul>
                                  </div>
                                )}

                                {job.preferred_qualifications && (
                                  <div>
                                    <span className="font-bold">
                                      Preferred Qualifications:
                                    </span>
                                    <ul className="ml-4 mt-2 list-disc space-y-1 text-base-content/80">
                                      {parseArrayField(
                                        job.preferred_qualifications,
                                      ).map((item, idx) => (
                                        <li key={idx}>{item.trim()}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {job.benefits && (
                                  <div>
                                    <span className="font-bold">Benefits:</span>
                                    <ul className="ml-4 mt-2 list-disc space-y-1 text-base-content/80">
                                      {parseArrayField(job.benefits).map(
                                        (item, idx) => (
                                          <li key={idx}>{item.trim()}</li>
                                        ),
                                      )}
                                    </ul>
                                  </div>
                                )}

                                {job.application_url && (
                                  <div className="pt-4">
                                    <p className="text-sm">
                                      <span className="font-bold">
                                        Application Instructions:
                                      </span>{" "}
                                      {job.application_url}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <div className="join">
                      <button
                        className="btn join-item"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        «
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <button
                            className={`btn join-item ${currentPage === page ? "btn-active" : ""}`}
                            key={page}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        ),
                      )}
                      <button
                        className="btn join-item"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        »
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="py-16 text-center">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-base-300">
                  <Search className="text-base-content/50" size={40} />
                </div>
                <h3 className="mb-2 text-2xl font-bold">No positions found</h3>
                <p className="mb-4 text-base-content/70">
                  Try adjusting your search or filters to find more
                  opportunities
                </p>
                {hasActiveFilters && (
                  <button className="btn btn-primary" onClick={clearFilters}>
                    Clear All Filters
                  </button>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Benefits */}
        <section className="bg-base-100 py-16" id="life-at-hareeng">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Why Work With Us?</h2>
              <p className="text-xl text-base-content/70">
                Benefits and perks that support your growth
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {benefits.map((benefit, index) => (
                <div className="card bg-base-200 shadow-lg" key={index}>
                  <div className="card-body text-center">
                    <div className="mx-auto mb-4">{benefit.icon}</div>
                    <h3 className="card-title justify-center">
                      {benefit.title}
                    </h3>
                    <p className="text-base-content/70">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-2">
              <div className="card border border-primary/20 bg-primary/10">
                <div className="card-body">
                  <Laptop className="mb-4 text-primary" size={32} />
                  <h3 className="card-title text-primary">
                    Remote-First Culture
                  </h3>
                  <p>
                    Work from anywhere in Africa with flexible hours and home
                    office stipend
                  </p>
                </div>
              </div>

              <div className="card border border-success/20 bg-success/10">
                <div className="card-body">
                  <Sparkles className="mb-4 text-success" size={32} />
                  <h3 className="card-title text-success">
                    Learning & Development
                  </h3>
                  <p>
                    Annual learning budget, conference attendance, and
                    mentorship programs
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Company Values */}
        <section className="py-16 bg-base-100">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Our Values</h2>
              <p className="text-xl text-base-content/70">
                The principles that guide how we work together
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {values.map((value, index) => (
                <div className="text-center" key={index}>
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-base-100 shadow-lg">
                    {value.icon}
                  </div>
                  <h3 className="mb-2 text-xl font-semibold">{value.title}</h3>
                  <p className="text-base-content/70">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </>
  );
}
